import "@/App.css";
import { useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, AuthContext } from "./context/AuthContext";
import { canSee, PORTAL_ROLES } from "./access";
import DashboardLayout from "./components/DashboardLayout";
import UserTopNav from "./components/UserTopNav";
import LoginPage from "./pages/LoginPage";
import UserLoginPage from "./pages/UserLoginPage";
import DashboardPage from "./pages/DashboardPage";
import SubjectsPage from "./pages/master-data/SubjectsPage";
import MedimagicLessonsPage from "./pages/master-data/MedimagicLessonsPage";
import NMCLessonsPage from "./pages/master-data/NMCLessonsPage";
import CollegesPage from "./pages/master-data/CollegesPage";
import CountriesPage from "./pages/master-data/CountriesPage";
import StatesPage from "./pages/master-data/StatesPage";
import CitiesPage from "./pages/master-data/CitiesPage";
import UniversitiesPage from "./pages/master-data/UniversitiesPage";
import UsersPage from "./pages/UsersPage";
import TicketsPage from "./pages/TicketsPage";
import ContactPage from "./pages/ContactPage";
import AdminTimetablePage from "./pages/AdminTimetablePage";
import NotificationsPage from "./pages/NotificationsPage";
import SettingsPage from "./pages/SettingsPage";
import FacultyPage    from "./pages/user-portal/FacultyPage";
import CalendarPage from "./pages/user-portal/CalendarPage";
import PortalTimetableRoute from "./pages/user-portal/PortalTimetableRoute";
import UserTicketsPage from "./pages/user-portal/UserTicketsPage";
import PortalCoursesPage from "./pages/user-portal/PortalCoursesPage";
import PortalMasterDataPage from "./pages/user-portal/PortalMasterDataPage";
import PortalUsersPage from "./pages/user-portal/PortalUsersPage";
import PortalContactPage from "./pages/user-portal/PortalContactPage";

const ADMIN_ROLES = ["admin"];

// Inside the portal, support_user gets the full triage view (TicketsPage),
// while raisers (IT/college admin etc.) get the create-and-track view.
function PortalTicketsRoute() {
  // All portal users (including support_user) use UserTicketsPage.
  // UserTicketsPage shows triage controls for support roles and
  // a create-ticket button for all portal users.
  return <UserTicketsPage />;
}

function LoadingScreen() {
  return (
    <div className="app-loading">
      <div className="app-loading-inner">
        <svg width="52" height="52" viewBox="0 0 80 80" fill="none">
          <path d="M40 6 L74 40 L40 74 L6 40 Z" stroke="#3072FF" strokeWidth="5.5" fill="none" strokeLinejoin="round" />
          <polyline points="12,40 25,40 31,20 40,60 49,40 62,40 68,40" stroke="#43E0FF" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="app-loading-text">Loading…</span>
      </div>
    </div>
  );
}

// Super Admin panel — only role "admin"
function AdminLayoutRoute() {
  const { user } = useContext(AuthContext);
  if (user === undefined) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (PORTAL_ROLES.includes(user.role)) return <Navigate to="/users/dashboard" replace />;
  return <DashboardLayout />;
}

// Shared portal shell — for all non-admin roles.
// If a `moduleKey` is supplied, the page is rendered only when the role has access;
// otherwise the user is redirected to their dashboard.
function PortalShell({ children, moduleKey }) {
  const { user } = useContext(AuthContext);
  if (user === undefined) return <LoadingScreen />;
  if (!user) return <Navigate to="/users/login" replace />;
  if (ADMIN_ROLES.includes(user.role)) return <Navigate to="/dashboard" replace />;
  if (!PORTAL_ROLES.includes(user.role)) return <Navigate to="/users/login" replace />;
  if (moduleKey && !canSee(user.role, moduleKey)) {
    return <Navigate to="/users/dashboard" replace />;
  }
  return (
    <div className="dash-root">
      <UserTopNav />
      <main className="dash-content">{children}</main>
    </div>
  );
}

// /users/dashboard — entry point for all portal roles.
// Every portal role now sees the unified Dashboard (formerly Quick View).
// Scoping happens server-side based on the user's role.
function UserDashboardRoute() {
  return (
    <PortalShell moduleKey="dashboard">
      <DashboardPage />
    </PortalShell>
  );
}

// Only IT Admin gets the Faculty management page.
function ITAdminOnly({ children }) {
  const { user } = useContext(AuthContext);
  if (user === undefined) return <LoadingScreen />;
  if (!user) return <Navigate to="/users/login" replace />;
  if (user.role !== "it_admin") return <Navigate to="/users/dashboard" replace />;
  return (
    <div className="dash-root">
      <UserTopNav />
      <main className="dash-content">{children}</main>
    </div>
  );
}

// IT Admin and Faculty can both access the Calendar page.
function ITAdminOrFaculty({ children }) {
  const { user } = useContext(AuthContext);
  if (user === undefined) return <LoadingScreen />;
  if (!user) return <Navigate to="/users/login" replace />;
  if (user.role !== "it_admin" && user.role !== "faculty") return <Navigate to="/users/dashboard" replace />;
  return (
    <div className="dash-root">
      <UserTopNav />
      <main className="dash-content">{children}</main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public auth */}
          <Route path="/login"       element={<LoginPage />} />
          <Route path="/users/login" element={<UserLoginPage />} />

          {/* User portal — shared across all non-admin roles */}
          <Route path="/users/dashboard"     element={<UserDashboardRoute />} />
          <Route path="/users/notifications" element={<PortalShell moduleKey="notifications"><NotificationsPage /></PortalShell>} />
          <Route path="/users/courses"       element={<PortalShell moduleKey="courses"><PortalCoursesPage /></PortalShell>} />
          <Route path="/users/master-data"   element={<PortalShell moduleKey="master_data"><PortalMasterDataPage /></PortalShell>} />
          <Route path="/users/master-data/*" element={<PortalShell moduleKey="master_data"><PortalMasterDataPage /></PortalShell>} />
          <Route path="/users/users"         element={<PortalShell moduleKey="users"><PortalUsersPage /></PortalShell>} />
          <Route path="/users/timetable"     element={<PortalShell moduleKey="timetable"><PortalTimetableRoute /></PortalShell>} />
          <Route path="/users/tickets"       element={<PortalShell moduleKey="tickets"><PortalTicketsRoute /></PortalShell>} />
          <Route path="/users/contact"       element={<PortalShell moduleKey="contact"><PortalContactPage /></PortalShell>} />

          {/* IT-Admin-only pages */}
          <Route path="/users/faculty"    element={<ITAdminOnly><FacultyPage    /></ITAdminOnly>} />
          {/* IT-Admin + Faculty pages */}
          <Route path="/users/calendar"   element={<ITAdminOrFaculty><CalendarPage /></ITAdminOrFaculty>} />

          {/* Super Admin panel */}
          <Route element={<AdminLayoutRoute />}>
            <Route path="/dashboard"                     element={<DashboardPage />} />
            <Route path="/master-data/countries"         element={<CountriesPage />} />
            <Route path="/master-data/states"            element={<StatesPage />} />
            <Route path="/master-data/cities"            element={<CitiesPage />} />
            <Route path="/master-data/universities"      element={<UniversitiesPage />} />
            <Route path="/master-data/subjects"          element={<SubjectsPage />} />
            <Route path="/master-data/medimagic-lessons" element={<MedimagicLessonsPage />} />
            <Route path="/master-data/nmc-lessons"       element={<NMCLessonsPage />} />
            <Route path="/master-data/colleges"          element={<CollegesPage />} />
            <Route path="/users"                         element={<UsersPage />} />
            <Route path="/timetable"                     element={<AdminTimetablePage />} />
            <Route path="/tickets"                       element={<TicketsPage />} />
            <Route path="/contact"                       element={<ContactPage />} />
            <Route path="/notifications"                 element={<NotificationsPage />} />
            <Route path="/settings"                      element={<SettingsPage />} />
          </Route>

          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
